/**
 * BulletinQASearch — compact universal Q&A bar on top of the bulletin board.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import { Sparkles, Search, ExternalLink, ThumbsUp, ThumbsDown, X } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function BulletinQASearch() {
  const { activeWorkspaceId: wsId } = useAuth();
  const { can } = useWorkspacePerms();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!can("qa.ask")) return null;

  const ask = async () => {
    if (!q.trim()) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/workspaces/${wsId}/qa/ask`, { question: q });
      setResult(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Ask failed")); }
    finally { setBusy(false); }
  };
  const feedback = async (helpful) => {
    if (!result?.log_id) return;
    try {
      await api.post(`/qa/ask-log/${result.log_id}/feedback`, { helpful });
      toast.success(helpful ? "Thanks!" : "Thanks — we'll review");
      setResult({ ...result, _rated: true });
    } catch { /* silent */ }
  };

  return (
    <div className="border border-border bg-background overflow-hidden"
         data-testid="bulletin-qa-search">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-gradient-to-r from-indigo-50 to-purple-50">
        <Sparkles className="w-4 h-4 text-indigo-600" />
        <span className="text-[11px] uppercase tracking-widest font-semibold text-indigo-700">Company Q&A</span>
        <Link to="/app/company-qa" className="ml-auto text-[10px] text-indigo-700 underline">
          Configure →
        </Link>
      </div>
      <div className="p-3">
        <div className="flex gap-2">
          <input
            className="flex-1 border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-400"
            placeholder="Ask anything — policies, benefits, security, tools…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ask()}
            data-testid="bulletin-qa-input"
          />
          <button onClick={ask} disabled={busy || !q.trim()} data-testid="bulletin-qa-submit"
                   className="text-sm font-semibold px-3 py-2 bg-indigo-600 text-white disabled:opacity-60 inline-flex items-center gap-1">
            <Search className="w-3.5 h-3.5" /> {busy ? "…" : "Ask"}
          </button>
        </div>

        {result && (
          <div className="mt-3 border border-slate-200 bg-slate-50 p-3 relative" data-testid="bulletin-qa-result">
            <button onClick={() => { setResult(null); setQ(""); }}
                     className="absolute top-1 right-1 text-slate-400 hover:text-slate-700"
                     aria-label="Close"
                     data-testid="bulletin-qa-close">
              <X className="w-3.5 h-3.5" />
            </button>
            {!result.grounded ? (
              <div className="text-xs text-slate-700 pr-4">
                <div className="font-semibold text-indigo-700 mb-1">✨ We got your question.</div>
                We couldn&apos;t find a confident answer in the currently indexed sources yet,
                but this question is now logged for your admins to review and train the assistant.
                You&apos;ll get better answers as more content is added.
              </div>
            ) : (
              <>
                <div className="text-sm text-slate-800 whitespace-pre-wrap pr-4"
                     data-testid="bulletin-qa-answer">{result.answer}</div>
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] uppercase tracking-widest text-muted-foreground mr-1">
                    Confidence {(result.confidence * 100).toFixed(0)}%
                  </span>
                  {(result.sources || []).filter((s) => s.cited).slice(0, 4).map((s, i) => (
                    <a key={i} href={s.ref_url}
                       className="text-[10px] px-1.5 py-0.5 border border-slate-300 hover:bg-slate-100 inline-flex items-center gap-1">
                      {s.source_label}
                      <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  ))}
                  {!result._rated && (
                    <div className="ml-auto flex items-center gap-1">
                      <button onClick={() => feedback(true)} data-testid="bulletin-qa-fb-yes"
                               className="text-[10px] px-1.5 py-0.5 border border-slate-300 hover:bg-emerald-50"><ThumbsUp className="w-2.5 h-2.5" /></button>
                      <button onClick={() => feedback(false)} data-testid="bulletin-qa-fb-no"
                               className="text-[10px] px-1.5 py-0.5 border border-slate-300 hover:bg-red-50"><ThumbsDown className="w-2.5 h-2.5" /></button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
